/**
 * history.ts
 * ----------------
 * Selector functions for live site event history view across portals.
 */

import { LiveSiteEventItem } from '../../models/ui/liveSiteEvent';
import { PortalState } from '../../reducers/shared';
import { isUndefinedOrWhiteSpace } from '../../utilities/strings';
import { getLocalDateFromUtcTimestamp } from '../../utilities/time';

/**
 * Private function
 */

function dateComparer (a: Date, b: Date) {
    if (a < b) {
        return -1;
    }

    if (a > b) {
        return 1;
    }

    return 0;
}

/**
 * Select current set of active live site events.
 * @param state Current application sub-state
 * @returns The current set of active live site events
 */
export function selectActiveLiveSiteEvents (state: PortalState) {
    const { activeLiveSiteEventsResponse } = state.history;

    if (activeLiveSiteEventsResponse === undefined) {
        return [];
    }

    return activeLiveSiteEventsResponse.liveSiteEvents.map<LiveSiteEventItem>(liveSiteEvent => ({
        id: liveSiteEvent.id,
        impactStartTimestamp: liveSiteEvent.impactStartTimestamp,
        impacts: liveSiteEvent.impacts.map(impact => ({ geography: impact.geography, state: impact.state })),
        lastUpdateTimestamp: liveSiteEvent.lastUpdatedTimestamp,
        service: liveSiteEvent.impacts.map(impact => impact.service)[0],
        severity: liveSiteEvent.severity,
        title: liveSiteEvent.title,
    })).sort((a, b) => {
        // Default sort will be descending by start time
        const aDate = getLocalDateFromUtcTimestamp(a.impactStartTimestamp);
        const bDate = getLocalDateFromUtcTimestamp(b.impactStartTimestamp);

        return dateComparer(aDate, bDate) * -1;
    });
}

/**
 * Select the current set of geography filter values.
 * @param state Current application sub-state
 * @returns The current set of geography filter values
 */
export function selectFilteredGeographies (state: PortalState) {
    return state.history.geographies;
}

/**
 * Select the current set of service filter values.
 * @param state Current application sub-state
 * @returns The current set of service filter values
 */
export function selectFilteredServices (state: PortalState) {
    return state.history.services;
}

/**
 * Select the current set of severity filter values.
 * @param state Current application sub-state
 * @returns The current set of severity filter values
 */
export function selectFilteredSeverities (state: PortalState) {
    return state.history.severities;
}

/**
 * Select the current time filter value.
 * @param state Current application sub-state
 * @returns The current time filter value
 */
export function selectFilteredTime (state: PortalState) {
    return state.history.time;
}

/**
 * Select the error for GET active live site events, if any
 * @param state Current application state
 * @returns Current error for the GET active live site events request, if any
 */
export function selectGetActiveLiveSiteEventsError (state: PortalState) {
    return state.history.getActiveLiveSiteEventsError;
}

/**
 * Select the request status for GET active live site events
 * @param state Current application state
 * @returns Current request status for the GET active live site events request
 */
export function selectGetActiveLiveSiteEventsStatus (state: PortalState) {
    return state.history.getActiveLiveSiteEventsStatus;
}

/**
 * Select the error for GET active live site events, if any
 * @param state Current application state
 * @returns Current error for the GET active live site events request, if any
 */
export function selectGetResolvedLiveSiteEventsError (state: PortalState) {
    return state.history.getResolvedLiveSiteEventsError;
}

/**
 * Select the request status for GET active live site events
 * @param state Current application state
 * @returns Current request status for the GET active live site events request
 */
export function selectGetResolvedLiveSiteEventsStatus (state: PortalState) {
    return state.history.getResolvedLiveSiteEventsStatus;
}

/**
 * Select current set of resolved live site events, filtered by currently applied filters.
 * @param state Current application sub-state
 * @returns The current set of resolved live site events with filters applied
 */
export function selectResolvedLiveSiteEvents (state: PortalState) {
    const { geographies, resolvedLiveSiteEventsResponse, search, services, severities } = state.history;

    if (resolvedLiveSiteEventsResponse === undefined) {
        return [];
    }

    let { liveSiteEvents } = resolvedLiveSiteEventsResponse;

    // Apply search string filter first, as it should winnow the field the most
    if (!isUndefinedOrWhiteSpace(search)) {
        liveSiteEvents = liveSiteEvents.filter(liveSiteEvent => liveSiteEvent.title.includes(search));
    }

    if (severities.length > 0) {
        liveSiteEvents = liveSiteEvents.filter(liveSiteEvent => severities.includes(liveSiteEvent.severity));
    }

    // Save impact-based filters for last, as they're quadratic searches
    if (geographies.length > 0) {
        liveSiteEvents = liveSiteEvents.filter(liveSiteEvent => {
            return geographies.some(geo => liveSiteEvent.impacts.find(impact => impact.geography === geo));
        });
    }

    if (services.length > 0) {
        liveSiteEvents = liveSiteEvents.filter(liveSiteEvent => {
            return services.some(service => liveSiteEvent.impacts.find(impact => impact.service === service));
        });
    }

    return liveSiteEvents.map<LiveSiteEventItem>(liveSiteEvent => {
        const resolvedTimestamp = [...liveSiteEvent.impacts].sort((a, b) => {
            const aDate = getLocalDateFromUtcTimestamp(a.resolvedTimestamp as string);
            const bDate = getLocalDateFromUtcTimestamp(b.resolvedTimestamp as string);

            return dateComparer(aDate, bDate);
        }).reverse()[0].resolvedTimestamp;

        return {
            id: liveSiteEvent.id,
            impactStartTimestamp: liveSiteEvent.impactStartTimestamp,
            impacts: liveSiteEvent.impacts.map(impact => ({ geography: impact.geography, state: impact.state })),
            lastUpdateTimestamp: liveSiteEvent.lastUpdatedTimestamp,
            resolvedTimestamp,
            service: liveSiteEvent.impacts.map(impact => impact.service)[0],
            severity: liveSiteEvent.severity,
            title: liveSiteEvent.title,
        };
    }).sort((a, b) => {
        // Default sort will be descending by start time
        const aDate = getLocalDateFromUtcTimestamp(a.impactStartTimestamp);
        const bDate = getLocalDateFromUtcTimestamp(b.impactStartTimestamp);

        return dateComparer(aDate, bDate) * -1;
    });
}

/**
 * Selects the current search string.
 * @param state Current application sub-state
 * @returns The current search string
 */
export function selectSearch (state: PortalState) {
    return state.history.search;
}
